<template>
  <div>
    <b-overlay :show="loading">
      <b-card no-body class="mb-0">
        <div class="m-2">
          <b-row class="align-items-center mb-1">
            <b-col cols="12" md="6" class="d-flex align-items-center mb-1 mb-md-0">
              <label>Mostrar</label>
              <v-select
                v-model="perPage"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50"
              />
              <label>entradas</label>
            </b-col>
            <b-col cols="12" md="6">
              <div class="d-flex align-items-center justify-content-end" style="gap:8px">
                <b-input-group>
                  <b-form-input
                    v-model="searchInputValue"
                    placeholder="Pesquisar por código ou descrição..."
                    @keyup.enter="handleSearch"
                  />
                  <b-input-group-append>
                    <b-button variant="outline-danger" @click="clearSearch">
                      <feather-icon icon="TrashIcon" />
                    </b-button>
                    <b-button variant="outline-primary" @click="handleSearch">
                      <feather-icon icon="SearchIcon" />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
                <b-button variant="outline-success" @click="$bvModal.show('modal-importar-csv')">
                  <feather-icon icon="UploadIcon" class="mr-50" />
                  Importar CSV
                </b-button>
                <b-button variant="outline-info" @click="onConsultarEstoqueVisible">
                  <feather-icon icon="RefreshCwIcon" class="mr-50" />
                  Atualizar Estoque
                </b-button>
              </div>
            </b-col>
          </b-row>
        </div>

        <b-table
          ref="refTable"
          class="position-relative"
          :items="fetchItems"
          :fields="tableColumns"
          :per-page="perPage"
          :current-page="currentPage"
          primary-key="item_id"
          :sort-by.sync="sortBy"
          :sort-desc.sync="isSortDirDesc"
          show-empty
          empty-text="Nenhum registro encontrado"
          responsive
        >
          <template #cell(item_id)="data">
            <span class="font-weight-bold">{{ data.item.item_id }}</span>
            <small v-if="data.item.codigosubstituto" class="d-block text-muted">
              Subst: {{ data.item.codigosubstituto }}
            </small>
          </template>

          <template #cell(estoque)="data">
            <estoque-badge :estoque-info="estoqueMap[data.item.item_id]" />
          </template>

          <template #cell(certificacao)="data">
            <b-badge :variant="data.item.certificacao ? 'success' : 'secondary'">
              {{ data.item.certificacao ? 'Sim' : 'Não' }}
            </b-badge>
          </template>

          <template #cell(dataprevista)="data">
            <span>{{ data.item.dataprevista || '—' }}</span>
          </template>

          <template #cell(actions)="data">
            <b-button
              variant="gradient-primary"
              class="btn-icon"
              size="sm"
              @click="openEdit(data.item)"
              title="Editar estoque"
              v-b-tooltip.hover
            >
              <feather-icon icon="EditIcon" />
            </b-button>
          </template>
        </b-table>

        <div class="mx-2 mb-2">
          <b-row>
            <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
              <span class="text-muted">
                Mostrando {{ dataMeta.from }} até {{ dataMeta.to }} de {{ dataMeta.of }} entradas
              </span>
            </b-col>
            <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
              <b-pagination
                v-model="currentPage"
                :total-rows="totalItems"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text><feather-icon icon="ChevronLeftIcon" size="18" /></template>
                <template #next-text><feather-icon icon="ChevronRightIcon" size="18" /></template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </b-overlay>

    <b-modal id="modal-importar-csv" title="Importar Estoque via CSV" ok-title="Importar" cancel-title="Cancelar" @ok.prevent="handleImportarCsv" :ok-disabled="!csvFile">
      <div>
        <p class="text-muted mb-1">O arquivo CSV deve conter as colunas:</p>
        <code>item_id, estoque, certificacao, dataprevista, codigosubstituto</code>
        <p class="text-muted mt-1 mb-1">Apenas <strong>item_id</strong> e <strong>estoque</strong> são obrigatórios.</p>
        <b-form-file
          v-model="csvFile"
          accept=".csv"
          placeholder="Selecione o arquivo CSV..."
          browse-text="Procurar"
          class="mt-1"
        />
        <b-alert :show="importResult !== null" :variant="importResult && importResult.errors === 0 ? 'success' : 'warning'" class="mt-1">
          <span v-if="importResult">
            <strong>{{ importResult.updated }}</strong> atualizados,
            <strong>{{ importResult.errors }}</strong> erros.
            <span v-if="importResult.erroDetails && importResult.erroDetails.length">
              <br>Itens com erro: {{ importResult.erroDetails.map(e => e.item_id).join(', ') }}
            </span>
          </span>
        </b-alert>
      </div>
    </b-modal>

    <b-modal id="modal-edit-estoque" title="Editar Informações de Estoque" ok-title="Salvar" cancel-title="Cancelar" @ok.prevent="handleSaveEdit">
      <b-form v-if="editItem">
        <b-form-group label="Item ID">
          <b-form-input :value="editItem.item_id" disabled />
        </b-form-group>
        <b-form-group label="Estoque (fallback)">
          <b-form-input v-model.number="editItem.estoque" type="number" placeholder="Quantidade" />
        </b-form-group>
        <b-form-group label="Certificação (estoque infinito)">
          <b-form-checkbox v-model="editItem.certificacao" :value="1" :unchecked-value="0">
            Produto certificado
          </b-form-checkbox>
        </b-form-group>
        <b-form-group label="Data Prevista de Disponibilidade">
          <b-form-input v-model="editItem.dataprevista" type="date" />
        </b-form-group>
        <b-form-group label="Código Substituto">
          <b-form-input v-model="editItem.codigosubstituto" placeholder="Ex: 9788582762487" />
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BButton, BBadge, BPagination,
  BOverlay, BFormInput, BInputGroup, BInputGroupAppend,
  BModal, BFormFile, BAlert, BForm, BFormGroup, BFormCheckbox,
  VBTooltip,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import { ref, computed } from '@vue/composition-api';
import SLSItemService from '@/services/SistemaDKP/SLSItem.service';
import SLSEstoqueService from '@/services/SistemaDKP/SLSEstoque.service';
import EstoqueBadge from '@/components/EstoqueBadge.vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

const itemService = new SLSItemService();
const estoqueService = new SLSEstoqueService();

export default {
  name: 'EstoqueList',
  components: {
    BCard, BRow, BCol, BTable, BButton, BBadge, BPagination,
    BOverlay, BFormInput, BInputGroup, BInputGroupAppend,
    BModal, BFormFile, BAlert, BForm, BFormGroup, BFormCheckbox,
    vSelect,
    EstoqueBadge,
  },
  directives: { 'b-tooltip': VBTooltip },
  data() {
    return {
      loading: false,
      perPage: 25,
      perPageOptions: [10, 25, 50, 100],
      currentPage: 1,
      totalItems: 0,
      sortBy: 'item_id',
      isSortDirDesc: false,
      searchInputValue: '',
      searchQuery: '',
      tableColumns: [
        { key: 'item_id', label: 'Código', sortable: true },
        { key: 'item_descricao', label: 'Descrição', sortable: true },
        { key: 'estoque', label: 'Estoque', sortable: false },
        { key: 'certificacao', label: 'Cert.', sortable: false },
        { key: 'dataprevista', label: 'Data Prevista', sortable: false },
        { key: 'actions', label: 'Ações', sortable: false },
      ],
      estoqueMap: {},
      csvFile: null,
      importResult: null,
      editItem: null,
    };
  },
  computed: {
    dataMeta() {
      const from = this.perPage * (this.currentPage - 1) + 1;
      const to = Math.min(this.perPage * this.currentPage, this.totalItems);
      return { from: this.totalItems ? from : 0, to: this.totalItems ? to : 0, of: this.totalItems };
    },
  },
  methods: {
    async fetchItems(ctx, callback) {
      this.loading = true;
      try {
        const response = await itemService.findAll({
          q: this.searchQuery,
          perPage: this.perPage,
          page: this.currentPage,
          sortBy: this.sortBy,
          sortDesc: this.isSortDirDesc,
          fullObject: true,
        });
        const items = response.data || [];
        this.totalItems = response.total || 0;
        callback(items);

        const ids = items.map(i => i.item_id);
        if (ids.length) {
          estoqueService.consultarEstoque(ids).then(map => {
            this.estoqueMap = { ...this.estoqueMap, ...map };
          });
        }
      } catch (e) {
        callback([]);
        this.$toast({ component: ToastificationContent, props: { title: 'Erro', text: 'Erro ao carregar itens', icon: 'AlertTriangleIcon', variant: 'danger' } });
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.searchQuery = this.searchInputValue;
      this.$refs.refTable.refresh();
    },
    clearSearch() {
      this.searchInputValue = '';
      this.searchQuery = '';
      this.$refs.refTable.refresh();
    },
    async onConsultarEstoqueVisible() {
      const items = this.$refs.refTable?.localItems || [];
      if (!items.length) return;
      const ids = items.map(i => i.item_id);
      estoqueService.limparCache();
      const map = await estoqueService.consultarEstoque(ids);
      this.estoqueMap = { ...this.estoqueMap, ...map };
      this.$toast({ component: ToastificationContent, props: { title: 'Estoque atualizado', icon: 'CheckIcon', variant: 'success' } });
    },
    async handleImportarCsv() {
      if (!this.csvFile) return;
      this.loading = true;
      try {
        const result = await estoqueService.importarCsv(this.csvFile);
        this.importResult = result;
        if (result.updated > 0) {
          estoqueService.limparCache();
          this.$refs.refTable.refresh();
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Importação concluída',
            text: `${result.updated} itens atualizados`,
            icon: 'CheckIcon',
            variant: result.errors === 0 ? 'success' : 'warning',
          },
        });
      } catch (e) {
        this.$toast({ component: ToastificationContent, props: { title: 'Erro', text: e.message || 'Erro ao importar', icon: 'AlertTriangleIcon', variant: 'danger' } });
      } finally {
        this.loading = false;
      }
    },
    openEdit(item) {
      this.editItem = { ...item };
      this.$bvModal.show('modal-edit-estoque');
    },
    async handleSaveEdit() {
      if (!this.editItem) return;
      this.loading = true;
      try {
        await itemService.updateItem(this.editItem.item_id, {
          estoque: this.editItem.estoque,
          certificacao: this.editItem.certificacao,
          dataprevista: this.editItem.dataprevista || null,
          codigosubstituto: this.editItem.codigosubstituto || null,
        });
        estoqueService.limparCache();
        this.$refs.refTable.refresh();
        this.$bvModal.hide('modal-edit-estoque');
        this.$toast({ component: ToastificationContent, props: { title: 'Salvo', icon: 'CheckIcon', variant: 'success' } });
      } catch (e) {
        this.$toast({ component: ToastificationContent, props: { title: 'Erro', text: 'Erro ao salvar', icon: 'AlertTriangleIcon', variant: 'danger' } });
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    currentPage() { this.$refs.refTable?.refresh(); },
    perPage() { this.$refs.refTable?.refresh(); },
  },
};
</script>

<style scoped>
.per-page-selector { width: 90px; }
</style>
